import { useState, useEffect, useRef } from 'react';
import { whatsappApi } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { Loader2, Plus, Edit2, Trash2, MessageSquare, Image, Video, Music, Mic, Sparkles, Info } from 'lucide-react';
import { getTypeLabel } from './constants';
import { usePageTitle } from '../../../hooks/usePageTitle';
import AITemplateModal from './AITemplateModal';

export default function TemplatesTab() {
  usePageTitle('Modelos de Mensagem');
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [variables, setVariables] = useState<Array<{ key: string; label: string; source: string }>>([]);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'text' as 'text' | 'image' | 'video' | 'audio' | 'voice',
    content: '',
    media_url: '',
    delay_type: 'auto' as 'auto' | 'random' | 'fixed',
    delay_seconds: 5
  });

  useEffect(() => {
    loadTemplates();
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      const response = await whatsappApi.getTemplateVariables();
      setVariables(response.variables || []);
    } catch (error) {
      // Non-critical, just ignore
    }
  };

  const insertVariable = (variable: string, targetRef: React.RefObject<HTMLTextAreaElement | null>) => {
    const textarea = targetRef.current;
    if (!textarea) {
      setFormData(prev => ({ ...prev, content: prev.content + variable }));
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const newText = text.substring(0, start) + variable + text.substring(end);
    setFormData(prev => ({ ...prev, content: newText }));
    // Restore cursor position after React re-render
    setTimeout(() => {
      textarea.focus();
      const newPos = start + variable.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await whatsappApi.getTemplates();
      setTemplates(response.templates || []);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingTemplate) {
        await whatsappApi.updateTemplate(editingTemplate.id, formData);
        toast.success('Template atualizado com sucesso');
      } else {
        await whatsappApi.createTemplate(formData);
        toast.success('Template criado com sucesso');
      }
      
      resetForm();
      loadTemplates();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao salvar template');
    }
  };

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      content: template.content || '',
      media_url: template.media_url || '',
      delay_type: template.delay_type || 'auto',
      delay_seconds: template.delay_seconds || 5
    });
    setShowForm(true);
  };

  const handleDelete = async (template: any) => {
    if (!window.confirm(`Tem certeza que deseja deletar o template "${template.name}"?`)) return;
    
    try {
      await whatsappApi.deleteTemplate(template.id);
      toast.success('Template deletado com sucesso');
      loadTemplates();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao deletar template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'text',
      content: '',
      media_url: '',
      delay_type: 'auto',
      delay_seconds: 5
    });
    setEditingTemplate(null);
    setShowForm(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'text': return <MessageSquare className="h-5 w-5" />;
      case 'image': return <Image className="h-5 w-5" />;
      case 'video': return <Video className="h-5 w-5" />;
      case 'audio': return <Music className="h-5 w-5" />;
      case 'voice': return <Mic className="h-5 w-5" />;
      default: return <MessageSquare className="h-5 w-5" />;
    }
  };

  

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Modelos de Mensagem</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAIModal(true)}
            className="inline-flex items-center px-4 py-2 text-white rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-200"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Criar com IA
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            Novo Template
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingTemplate ? 'Editar Template' : 'Novo Template'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Template *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Boas-vindas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Texto</option>
                  <option value="image">Imagem</option>
                  <option value="video">Vídeo</option>
                  <option value="audio">Áudio</option>
                  <option value="voice">Voz</option>
                </select>
              </div>
            </div>

            {formData.type === 'text' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem *
                </label>
                <textarea
                  ref={contentRef}
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite a mensagem... Use variáveis como {{nome}} para personalizar"
                />
                {/* Variables Panel */}
                {variables.length > 0 && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Info className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs font-semibold text-gray-600">Variáveis disponíveis</span>
                      <span className="text-xs text-gray-400">— clique para inserir</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {variables.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => insertVariable(v.key, contentRef)}
                          title={v.label}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono bg-white border border-gray-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400 transition-colors cursor-pointer"
                        >
                          {v.key}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL da Mídia *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.media_url}
                    onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://exemplo.com/arquivo.jpg"
                  />
                </div>
                
                {['image', 'video'].includes(formData.type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Legenda (opcional)
                    </label>
                    <textarea
                      ref={captionRef}
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Legenda da mídia... Use {{nome}} para personalizar"
                    />
                    {/* Variables Panel for captions */}
                    {variables.length > 0 && (
                      <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Info className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-xs font-semibold text-gray-600">Variáveis disponíveis</span>
                          <span className="text-xs text-gray-400">— clique para inserir</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {variables.map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertVariable(v.key, captionRef)}
                              title={v.label}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono bg-white border border-gray-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400 transition-colors cursor-pointer"
                            >
                              {v.key}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Delay
                </label>
                <select
                  value={formData.delay_type}
                  onChange={(e) => setFormData({ ...formData, delay_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="auto">Automático (baseado no tamanho)</option>
                  <option value="random">Aleatório (7-15s)</option>
                  <option value="fixed">Fixo</option>
                </select>
              </div>

              {formData.delay_type === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delay (segundos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.delay_seconds}
                    onChange={(e) => setFormData({ ...formData, delay_seconds: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                {editingTemplate ? 'Atualizar' : 'Criar'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>Nenhum template cadastrado</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="text-blue-600">
                        {getTypeIcon(template.type)}
                      </div>
                      <h4 className="font-semibold">{template.name}</h4>
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {getTypeLabel(template.type)}
                      </span>
                      {!template.is_active && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                          Inativo
                        </span>
                      )}
                    </div>
                    
                    {template.content && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {template.content}
                      </p>
                    )}
                    
                    {template.media_url && (
                      <p className="text-xs text-gray-500 truncate">
                        <strong>URL:</strong> {template.media_url}
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>Delay:</strong> {
                        template.delay_type === 'auto' ? 'Automático' :
                        template.delay_type === 'random' ? 'Aleatório (7-15s)' :
                        `Fixo (${template.delay_seconds}s)`
                      }
                    </p>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Deletar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Template Modal */}
      <AITemplateModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onSuccess={loadTemplates}
      />
    </div>
  );
}
